import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fetchPuzzle, fetchDailyPuzzle, makeGuess } from '../../api/puzzleService';
import GameBoard from '../board/GameBoard';
import Keyboard from '../board/Keyboard';
import DifficultyModal from '../difficulty/DifficultyModal';
import ScoreCalculationModal from '../info/ScoreCalculationModal';
import LoadingSpinner from '../shared/LoadingSpinner';
import { commonStyles } from '../../theme';


const CompleteMessage = styled.div`
  background-color: var(--color-success-bg, #d4edda);
  color: var(--color-success-text, #155724);
  border: 1px solid var(--color-success-border, #c3e6cb);
  padding: 16px 20px;
  border-radius: 8px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 20px auto;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 8px;
    max-width: none;
    z-index: 100;
  }
`;

const PlayMoreButton = styled.button`
  background-color: var(--color-button-primary, #6aaa64);
  color: var(--color-button-primary-text, #ffffff);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-button-primary-hover, #5d9556);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(106, 170, 100, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(106, 170, 100, 0.3);
  }

  &:focus {
    outline: 2px solid var(--color-focus, ${commonStyles.inputBorder});
    outline-offset: 2px;
  }
`;

const ScoreContainer = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  justify-self: center;
  margin: 0 auto;
`;

const InfoIcon = styled.button`
  background: none;
  border: none;
  color: var(--color-success-text, #155724);
  cursor: pointer;
  font-size: 0.7rem;
  padding: 1px;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  margin-top: -2px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 1px solid var(--color-focus, ${commonStyles.inputBorder});
    outline-offset: 1px;
  }
`;

const ErrorMessage = styled.div`
  background-color: var(--color-error-bg, #f8d7da);
  color: var(--color-error-text, #721c24);
  border: 1px solid var(--color-error-border, #f5c6cb);
  padding: 12px 16px;
  border-radius: 6px;
  margin: 12px auto;
  font-weight: 500;
  max-width: 600px;
  text-align: center;
`;

const GlobalStyles = styled.div`
  @media (max-width: 768px) {
    padding-bottom: 300px; /* Add space for fixed keyboard */
  }
`;

const PuzzleGame = () => {
  const { puzzleKeyParam, dateParam } = useParams();
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState('');
  const [showDifficultyModal, setShowDifficultyModal] = useState(true);
  const [puzzleKey, setPuzzleKey] = useState(null);
  const [puzzleState, setPuzzleState] = useState(null);
  const [encodedState, setEncodedState] = useState('');
  const [error, setError] = useState(null);
  const [currentGuess, setCurrentGuess] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [complete, setComplete] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittingGuess, setSubmittingGuess] = useState(false);

  useEffect(() => {
    const loadPuzzle = async () => {
      if (!difficulty) return; // Don't load puzzle until difficulty is selected
      
      setLoading(true);
      try {
        let response;
        if (dateParam) {
          // Load daily puzzle by date
          response = await fetchDailyPuzzle(dateParam, difficulty);
        } else if (puzzleKeyParam) {
          // Load puzzle by key
          response = await fetchPuzzle(puzzleKeyParam, difficulty);
        } else {
          setError('No puzzle identifier provided');
          return;
        }
        
        setPuzzleState(response.puzzle.starting_state);
        setActiveIndex(response.puzzle.starting_state.active_index);
        setEncodedState(response.encoded_state);
        setComplete(response.puzzle.starting_state.complete);
        setPuzzleKey(response.puzzle.key);
      } catch (err) {
        setError('Error fetching puzzle');
      } finally {
        setLoading(false);
      }
    };

    loadPuzzle();
  }, [puzzleKeyParam, dateParam, difficulty]);

  const isValidGuess = useCallback((guessToValidate) => {
    if (!puzzleState?.words || activeIndex < 0 || !puzzleState.words[activeIndex]) {
      setError('Invalid puzzle state');
      return false;
    }
    const activeWord = puzzleState.words[activeIndex].word || '';
    const fullGuess = activeWord + guessToValidate;
    
    if (!fullGuess){
      setError('Invalid guess');
      return false;
    }
    
    return true;
  }, [activeIndex, puzzleState?.words]);

  const handleGuessSubmit = useCallback(async (guessValue) => {
    setError(null);
    if (!isValidGuess(guessValue)) {
      return;
    }
    
    // Combine the existing word with the typed letters
    const activeWord = puzzleState?.words[activeIndex];
    const baseWord = activeWord?.word || '';
    const fullGuess = baseWord + guessValue;
    
    setSubmittingGuess(true);
    try {
      const response = await makeGuess(puzzleKey, fullGuess, encodedState);
      setPuzzleState(response.result.state);
      setActiveIndex(response.result.state.active_index);
      setEncodedState(response.encoded_state);
      setComplete(response.result.state.complete);
      setCurrentGuess(''); // Clear the guess after successful submission
    } catch (err) {
      setError('Error making guess');
    } finally {
      setSubmittingGuess(false);
    }
  }, [puzzleKey, encodedState, isValidGuess, activeIndex, puzzleState?.words]);

  const handleLetterPress = useCallback((letter) => {
    if (!puzzleState?.words || activeIndex < 0) return;
    
    const activeWord = puzzleState.words[activeIndex];
    const maxLength = activeWord.word_length || 12;
    const baseWord = activeWord.word || '';
    
    // Build from the existing word plus any additional letters typed
    const totalLength = baseWord.length + currentGuess.length;
    
    if (totalLength < maxLength) {
      setCurrentGuess(prev => prev + letter);
    }
  }, [currentGuess, activeIndex, puzzleState?.words]);

  const handleBackspace = useCallback(() => {
    if (currentGuess.length > 0) {
      setCurrentGuess(prev => prev.slice(0, -1));
    }
  }, [currentGuess]);

  // Calculate max additional letters that can be typed
  const getMaxLength = useCallback(() => {
    if (!puzzleState?.words || activeIndex < 0) return 12;
    const activeWord = puzzleState.words[activeIndex];
    const baseWordLength = activeWord?.word?.length || 0;
    const totalLength = activeWord?.word_length || 12;
    return Math.max(0, totalLength - baseWordLength);
  }, [puzzleState?.words, activeIndex]);

  const handleDifficultySelect = useCallback((selectedDifficulty) => {
    setDifficulty(selectedDifficulty);
    setShowDifficultyModal(false);
  }, []);
  
  return (
    <GlobalStyles>
      <DifficultyModal
        isOpen={showDifficultyModal}
        onDifficultySelect={handleDifficultySelect}
        onClose={() => navigate('/')}
      />

      <ScoreCalculationModal
        isOpen={showScoreModal}
        onClose={() => setShowScoreModal(false)}
        puzzleState={puzzleState}
      />

      {loading || !puzzleState ? (
        <>
          <LoadingSpinner padding="100px 0" />
        </>
      ) : (
        <GameBoard 
          puzzleState={puzzleState}
          activeIndex={activeIndex}
          showWordLength={puzzleState.settings?.show_word_length}
          showFirstLetter={puzzleState.settings?.show_first_letter}
          currentGuess={currentGuess}
        />
      )}
          
      {complete ? (
        <CompleteMessage>
          <div>Puzzle Complete!</div>
          {puzzleState.score !== undefined && (
            <ScoreContainer>
              <span>Score: {puzzleState.score}</span>
              <InfoIcon onClick={() => setShowScoreModal(true)} title="How is the score calculated?">
                ℹ️
              </InfoIcon>
            </ScoreContainer>
          )}
          <PlayMoreButton onClick={() => navigate('/')}>
            Play more
          </PlayMoreButton>
        </CompleteMessage>
      ) : (
        <Keyboard
          currentGuess={currentGuess}
          onLetterPress={handleLetterPress}
          onBackspace={handleBackspace}
          onSubmitGuess={handleGuessSubmit}
          disabled={loading || submittingGuess}
          maxLength={getMaxLength()}
          submittingGuess={submittingGuess}
        />
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </GlobalStyles>
  );
};

export default PuzzleGame;